import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChannels } from "../../hooks/useChannels";
import {
  Smartphone,
  Search,
  Plus,
  Settings,
  RefreshCw,
  TrendingUp,
  Users,
  Eye,
  Image,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import {
  getStatusColor,
  getProviderColor,
  formatDate,
} from "../../data/mockChannelData";

const ChannelsPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { channels, isLoading, error, refetch } = useChannels();

  // Filter channels based on search and status
  const filteredChannels = channels.filter((channel) => {
    const matchesSearch =
      channel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      channel.agent_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || channel.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: channels.length,
    active: channels.filter((c) => c.status === "active").length,
    totalConversations: channels.reduce(
      (sum, c) => sum + (c.total_conversations || 0),
      0
    ),
    totalEscalated: channels.reduce(
      (sum, c) => sum + (c.escalated_conversations || 0),
      0
    ),
  };

  const handleCreateChannel = () => {
    navigate("/admin/channels/create");
  };

  const handleViewDetail = (channelId) => {
    navigate(`/admin/channels/${channelId}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-lg text-gray-600 dark:text-gray-400">
              Loading channels...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl p-6 text-center">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            Failed to Load Channels
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-4">
            {error || "Something went wrong while loading your channels."}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Page Container */}
      <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
              <Smartphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              Channels
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your communication channels and integrations
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleCreateChannel}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Channel
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search channels by name or agent..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            />
          </div>

          <div className="sm:w-48 relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="pending">Pending Only</option>
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                  Total Channels
                </h4>
                <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                  {stats.total}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                  Active Channels
                </h4>
                <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                  {stats.active}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                  Total Conversations
                </h4>
                <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                  {stats.totalConversations}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                  Escalated
                </h4>
                <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                  {stats.totalEscalated}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Channels Grid */}
        {filteredChannels.length === 0 ? (
          // Empty State
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Smartphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
              {searchQuery || statusFilter !== "all"
                ? "No channels found"
                : "No channels yet"}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Create your first channel to start receiving messages from customers."}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <button
                onClick={handleCreateChannel}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus className="w-5 h-5" />
                Create Your First Channel
              </button>
            )}
          </div>
        ) : (
          // Channels Grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChannels.map((channel) => (
              <div
                key={channel.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-600 group flex flex-col h-full"
              >
                {/* Card Header */}
                <div className="p-6 pb-4 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          channel.status === "active"
                            ? "bg-green-500"
                            : channel.status === "pending"
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                      ></div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-lg ${getStatusColor(
                          channel.status
                        )}`}
                      >
                        {channel.status}
                      </span>
                    </div>

                    <button
                      onClick={() => handleViewDetail(channel.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <Settings className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                    </button>
                  </div>

                  {/* Channel Name & Type */}
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {channel.name}
                  </h3>

                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {channel.type.charAt(0).toUpperCase() +
                        channel.type.slice(1)}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {channel.agent_name}
                    </span>
                  </div>

                  {/* Model Provider */}
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getProviderColor(
                      channel.model_provider
                    )}`}
                  >
                    <div className="w-2 h-2 bg-current rounded-full"></div>
                    <span className="text-sm font-medium">
                      {channel.model_provider?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Metrics */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          CONVERSATIONS
                        </span>
                      </div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white">
                        {channel.total_conversations || 0}
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <AlertTriangle className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          ESCALATED
                        </span>
                      </div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white">
                        {channel.escalated_conversations || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                  {/* View Details Button - Full Width */}
                  <button
                    onClick={() => handleViewDetail(channel.id)}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                </div>

                {/* Status Footer */}
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-750 rounded-b-xl border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Created {formatDate(channel.created_at)}</span>
                    <div className="flex items-center gap-1">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          channel.status === "active"
                            ? "bg-green-400"
                            : channel.status === "pending"
                            ? "bg-yellow-400"
                            : "bg-red-400"
                        }`}
                      ></div>
                      <span className="capitalize">{channel.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ChannelsPage;
